import { useCallback, useEffect, useRef, useState } from "react";

import usePostRequest from "../../../hooks/usePostRequest";
import { API_ENDPOINTS } from "../../../constant/_constant";
import {
  buildEmptyAlterationValues,
  pickAlterationValues,
} from "./alterationFormConfig";

/** Decode the base64 PDF the generate endpoint returns into a Blob. */
const base64ToBlob = (base64, mimeType = "application/pdf") => {
  const binary = window.atob(base64);
  const chunkSize = 8192;
  const chunks = [];

  for (let offset = 0; offset < binary.length; offset += chunkSize) {
    const slice = binary.slice(offset, offset + chunkSize);
    const bytes = new Uint8Array(slice.length);
    for (let i = 0; i < slice.length; i += 1) {
      bytes[i] = slice.charCodeAt(i);
    }
    chunks.push(bytes);
  }

  return new Blob(chunks, { type: mimeType });
};

/** Wrap the callback-style usePostRequest in a promise. */
const request = (postData, payload, endpoint) =>
  new Promise((resolve, reject) => {
    postData(
      payload,
      (response) => resolve(response),
      (error) => reject(error || { message: "Request failed" }),
      endpoint,
    );
  });

/**
 * Data layer for the alteration form popup: loads the saved form (falling back
 * to RMA-derived prefill), saves it, and asks the backend to fill the PDF
 * template.
 *
 * The hook owns no UI decisions — `generatePdf` hands back a Blob and the
 * suggested file name, and the caller decides whether to preview or download.
 */
const useAlterationForm = (rmaId) => {
    const [values, setValues] = useState(buildEmptyAlterationValues);
    const [isDirty, setIsDirty] = useState(false);
    const [hasSavedForm, setHasSavedForm] = useState(false);
    const [loadError, setLoadError] = useState(null);

    /* Object URLs created for preview/download, revoked on unmount. */
    const objectUrlsRef = useRef([]);

    const { isLoading: isLoadingForm, postData: fetchForm } = usePostRequest(
        API_ENDPOINTS.GET_RMA_ALTERATION_FORM,
    );
    const { isLoading: isSaving, postData: postSaveForm } = usePostRequest(
        API_ENDPOINTS.SAVE_RMA_ALTERATION_FORM,
    );
    const { isLoading: isGenerating, postData: postGenerateForm } = usePostRequest(
        API_ENDPOINTS.GENERATE_RMA_ALTERATION_FORM,
    );

    useEffect(
        () => () => {
            objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
            objectUrlsRef.current = [];
        },
        [],
    );

    const setFieldValue = useCallback((key, value) => {
        setValues((current) => ({ ...current, [key]: value }));
        setIsDirty(true);
    }, []);

    const resetValues = useCallback((next) => {
        setValues(pickAlterationValues(next || {}));
        setIsDirty(false);
    }, []);

    /**
     * Load the saved form for this RMA. When nothing has been saved yet the
     * backend's RMA-derived prefill is used instead.
     */
    const loadForm = useCallback(async () => {
        if (!rmaId) {
            return null;
        }

        setLoadError(null);

        try {
            const response = await request(
                fetchForm,
                { rma_id: rmaId },
                API_ENDPOINTS.GET_RMA_ALTERATION_FORM,
            );

            const data = response?.data || {};
            const source = data.exists && data.form ? data.form : data.prefill;

            setHasSavedForm(Boolean(data.exists));
            setValues(pickAlterationValues(source || {}));
            setIsDirty(false);

            return data;
        } catch (error) {
            setLoadError(error?.message || "Unable to load the alteration form.");
            setValues(buildEmptyAlterationValues());
            setHasSavedForm(false);
            return null;
        }
    }, [fetchForm, rmaId]);

    const saveForm = useCallback(async () => {
        const response = await request(
            postSaveForm,
            { rma_id: rmaId, ...pickAlterationValues(values) },
            API_ENDPOINTS.SAVE_RMA_ALTERATION_FORM,
        );

        setHasSavedForm(true);
        setIsDirty(false);

        return response;
    }, [postSaveForm, rmaId, values]);

    /**
     * Fill the template on the backend and return the PDF as a Blob.
     *
     * @param {object} [options]
     * @param {boolean} [options.save=true]     Persist the submitted values too.
     * @param {boolean} [options.flatten=true]  false leaves the PDF fillable.
     */
    const generatePdf = useCallback(
        async (options = {}) => {
            const { save = true, flatten = true } = options;

            const response = await request(
                postGenerateForm,
                {
                    rma_id: rmaId,
                    save,
                    flatten,
                    ...pickAlterationValues(values),
                },
                API_ENDPOINTS.GENERATE_RMA_ALTERATION_FORM,
            );

            const data = response?.data || {};
            if (!data.base64) {
                throw new Error("The server did not return a PDF.");
            }

            const blob = base64ToBlob(data.base64, data.mime_type);
            const url = URL.createObjectURL(blob);
            objectUrlsRef.current.push(url);

            if (save) {
                setHasSavedForm(true);
                setIsDirty(false);
            }

            return { blob, url, fileName: data.file_name, size: data.size };
        },
        [postGenerateForm, rmaId, values],
    );

    return {
        values,
        setFieldValue,
        resetValues,
        isDirty,
        hasSavedForm,
        loadError,
        isLoadingForm,
        isSaving,
        isGenerating,
        loadForm,
        saveForm,
        generatePdf,
    };
};

export default useAlterationForm;
